<script setup lang="ts">
import logoImg from '@/assets/img/logo.png'
import NavButton from '@/components/button/NavButton.vue'
import Account from '@/components/account/Account.vue'
import { PrimeIcons } from '@primevue/core/api'
import { navItems } from '@/components/nav/NavData'
import { useViewportSize } from '@/components/composables/useViewportSize'
import Checkout from '@/components/checkout/Checkout.vue'
import { API } from '@/modules/api'
import { useI18n } from 'vue-i18n'
import LanguageSelector from '@/components/LanguageSelector.vue'
import { computed, onMounted, ref, watch } from 'vue'
import { useStore } from '@/stores'
import { useCartStore } from '@/stores/cart'
import { useRouter } from 'vue-router'
import { useSessionStore } from '@/stores/session'
import TieredMenu from 'primevue/tieredmenu'

const i18n = useI18n()
const store = useStore()
const cartStore = useCartStore()
const router = useRouter()
const sessionStore = useSessionStore()

let showLoggedInElements = ref(false)

const { is2XlOrGreater, isLgOrGreater, is3XlOrGreater, is4XlOrGreater, is5XlOrGreater } =
  useViewportSize()
const logout = async () => {
  await API.Post.logout()
  localStorage.removeItem('auth_token')
  sessionStore.$reset()
  store.$reset()
  cartStore.$reset()
  window.location.href = import.meta.env.VITE_LOBBY_URL
}

const numberOfCartItems = computed(() => {
  let numberOfCartItems = 0
  store.districts.forEach(district => {
    numberOfCartItems += cartStore.getCartLines(district.districtId).length
  })
  return numberOfCartItems
})

// Hide the cart entirely when every one of the family's districts has no way to
// accept online payments (no supported merchant account — DTB-343). Fail open:
// if any district accepts payments, or acceptingPayments is unknown, show it.
const showCart = computed(
  () => store.districts.length === 0 || store.districts.some(d => d.acceptingPayments !== false)
)

const nav = ref([] as any[])

const getDisplayName = (item: any) => {
  if (item.name && item.name.length <= 12) {
    return item.name
  }
  return i18n.t('c.nav.navData.label.' + item.code)
}

const toggleMenu = ref()

const toggle = (event: Event) => {
  toggleMenu.value?.toggle(event)
}

const maxNavItemsWithName = computed(() => {
  if (is5XlOrGreater.value) return 12
  if (is4XlOrGreater.value) return 9
  if (is3XlOrGreater.value) return 7
  if (is2XlOrGreater.value) return 6
  if (isLgOrGreater.value) return 5
  return 0 // fallback
})

const showName = computed(() => {
  return showLoggedInElements.value && nav.value.length < maxNavItemsWithName.value
})

onMounted(async () => {
  // If store is loaded, we can assume user is logged in (since they reached this component)
  if (store.isLoaded) {
    showLoggedInElements.value = true
  } else {
    showLoggedInElements.value = await store.loggedIn()
  }
})

watch(showLoggedInElements, async loggedIn => {
  if (loggedIn) {
    nav.value = await navItems()
  }
})
</script>

<template>
  <Toolbar class="DesktopNav">
    <template #start>
      <div class="DesktopNav_leftContainer">
        <NavButton class="w-full" route="/dashboard">
          <img class="DesktopNav_logo" :src="logoImg" :alt="$t('c.nav.dashboard')" />
        </NavButton>
      </div>
    </template>

    <template #center>
      <NavButton
        v-for="item in nav"
        :key="item.route"
        :name="showName ? getDisplayName(item) : undefined"
        :route="item.route"
        :app-id="item.appId"
        :img="item.img"
        :img-hover="item.imgHover"
        :alt="item.name !== null ? item.name : $t('c.nav.navData.label.' + item.code)"
      />
    </template>

    <template #end>
      <div class="DesktopNav_navGroup" v-if="showLoggedInElements">
        <div class="DesktopNav_rightContainer">
          <div class="ml-5 mr-5" v-if="showCart">
            <Checkout>
              <button
                class="DesktopNav_cartButton"
                :aria-label="$t('c.nav.shoppingCart')"
                type="button"
              >
                <OverlayBadge
                  v-if="numberOfCartItems > 0"
                  :value="numberOfCartItems"
                  size="small"
                  severity="success"
                >
                  <i class="pi pi-shopping-cart" style="font-size: 20px"></i>
                </OverlayBadge>
                <i v-else class="pi pi-shopping-cart" style="font-size: 20px"></i>
              </button>
            </Checkout>
          </div>

          <LanguageSelector class="mr-1" />

          <Account @logout="logout">
            <NavButton
              class="DesktopNav_accountBtn"
              :icon="PrimeIcons.USER"
              :alt="$t('c.nav.account')"
            >
              <span v-if="is2XlOrGreater" class="DesktopNav_accountText">{{
                $t('c.nav.account')
              }}</span>
            </NavButton>
          </Account>

          <div v-if="!isLgOrGreater && nav.length > 6">
            <Button
              type="button"
              icon="pi pi-bars"
              variant="text"
              @click="toggle($event)"
              aria-haspopup="true"
              aria-controls="overlay_tmenu"
            />
            <TieredMenu
              id="overlay_tmenu"
              ref="toggleMenu"
              :model="
                nav.map(item => ({
                  ...item,
                  label: getDisplayName(item),
                  command: () => router.push(item.route)
                }))
              "
              popup
            />
          </div>
        </div>
      </div>
      <div class="desktopNav_navGroup" v-else>
        <div class="DesktopNav_rightContainer">
          <LanguageSelector class="mr-1" />
        </div>
      </div>
    </template>
  </Toolbar>
</template>
<style scoped>
.DesktopNav {
  position: fixed;
  top: 0;
  right: 0;
  left: 0;
  border-radius: 0;
  padding: 2px var(--p-toolbar-padding);
  z-index: 1000;
}

.DesktopNav_navGroup {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  width: auto;
}

.DesktopNav_accountText {
  color: var(--p-green-600);
}

.DesktopNav_logo {
  width: 100%;
}

.DesktopNav_leftContainer {
  width: 190px;
}

.DesktopNav_rightContainer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.DesktopNav_cartButton {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: inherit;
}

.DesktopNav_cartButton:focus-visible {
  outline: 2px solid var(--p-primary-color, #0066cc);
  outline-offset: 2px;
  border-radius: 4px;
}

@media (min-width: 40rem) {
  /* sm - 640px */
}

@media (min-width: 48rem) {
  /* md - 768px */
}

@media (min-width: 64rem) {
  /* lg - 1024px */
}

@media (min-width: 75rem) {
  /* xl - 1200px */
}

@media (min-width: 80rem) {
  /* 2xl - 1280px */
  .DesktopNav_rightContainer {
    width: 340px;
  }
}
</style>
